// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import classNames from 'classnames';
import { Children, cloneElement, useId, useRef, useState } from 'react';
import { CLS_PREFIX } from '../../constants';
import ResponsiveContext from '../ResponsiveContext';
import FakeTabs from './FakeTabs';
import TabList from './TabList';
import { useActive } from './useActive';
import { useWidths } from './useWidths';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `Tabs` component is a content area with a group of individual [Tab](#!/Tab) elements, each displaying different content
 * in the same screen area. `Tabs` are used when you have too much content to display in one place at the same time, so
 * the content is broken into logical groups and each group is displayed under its own [Tab](#!/Tab) that serves both as a
 * group name and a navigation button.
 * @since 0.0.35
 */
const Tabs = _ref => {
  let {
    baseClassName = `${CLS_PREFIX}tabs`,
    className,
    addon,
    placeholder,
    children,
    active: externalActive,
    addonMinWidth,
    monospaced,
    search,
    ...props
  } = _ref;
  const addonElement = addon || placeholder;
  const tabNavRef = useRef(null);
  const tabListRef = useRef(null);
  const addonRef = useRef(null);
  const childrenCount = Children.toArray(children).length;
  const wrappedAddonElement = addonElement ? /*#__PURE__*/_jsx("div", {
    ref: addonRef,
    children: addonElement
  }) : null;
  const [active, setActive] = useActive(externalActive);
  const {
    monospacedWidth,
    recalculateWidths,
    compact,
    visibleLength
  } = useWidths({
    monospaced,
    tabNavRef,
    tabListRef,
    addonRef,
    baseClassName,
    childrenCount
  });
  const [searching, setSearching] = useState(false);
  const id = useId();
  if (!childrenCount) {
    return null;
  }
  const renderSearchBar = () => {
    if (!search) {
      return null;
    }
    const handleStartSearching = () => setSearching(true);
    const handleCancelSearching = () => setSearching(false);
    const searchProps = {
      className: `${baseClassName}__search-bar`,
      collapsable: compact,
      onExpand: handleStartSearching,
      onCollapse: handleCancelSearching,
      ...(searching ? {
        autoFocus: true,
        responsive: false,
        maxWidth: 'none',
        minWidth: 'unset'
      } : {})
    };
    const clone = /*#__PURE__*/cloneElement(search, searchProps);
    if (compact) {
      return /*#__PURE__*/_jsx(ResponsiveContext.Provider, {
        value: true,
        children: /*#__PURE__*/_jsx("div", {
          className: `${baseClassName}__search`,
          children: clone
        })
      });
    }
    return clone;
  };
  const addonStyle = {};
  if (search && !compact) {
    addonStyle.maxWidth = search.props.maxWidth;
  }
  const tabPanelId = `${id}-tabpanel`;
  return /*#__PURE__*/_jsxs("div", {
    className: classNames(baseClassName, className),
    ...props,
    children: [compact && wrappedAddonElement && /*#__PURE__*/_jsx("div", {
      className: `${baseClassName}__addon ${baseClassName}__addon--compact`,
      children: wrappedAddonElement
    }), /*#__PURE__*/_jsxs("div", {
      className: classNames(`${baseClassName}__nav`, compact && `${baseClassName}__nav--compact`, searching && `${baseClassName}__nav--searching`, search && `${baseClassName}__nav--search`),
      ref: tabNavRef,
      children: [/*#__PURE__*/_jsx(TabList, {
        tabListRef: tabListRef,
        active: active,
        onTabClick: setActive,
        visibleLength: visibleLength,
        monospacedWidth: monospacedWidth,
        baseClassName: baseClassName,
        tabPanelId: tabPanelId,
        children: children
      }), compact && renderSearchBar(), !compact && (wrappedAddonElement || search) && /*#__PURE__*/_jsxs("div", {
        className: `${baseClassName}__addon`,
        style: addonStyle,
        children: [renderSearchBar(), wrappedAddonElement]
      }), /*#__PURE__*/_jsx(FakeTabs, {
        onResize: recalculateWidths,
        baseClassName: baseClassName,
        children: children
      })]
    }), /*#__PURE__*/_jsx("div", {
      id: tabPanelId,
      className: `${baseClassName}__tab-panel`,
      role: "tabpanel",
      "aria-labelledby": `${id}-${active}`,
      children: Children.map(children, (tab, index) => index + 1 === active ? tab : null)
    })]
  });
};
export default Tabs;